import { useMemo } from 'react';
import { normalizeStatus } from '../utils/statuses';
import dayjs from 'dayjs';

const FOLLOW_UP_DAYS = 3;

export function useTriageGrouping(patients, searchQuery, activeFilter, selectedProviders) {
  return useMemo(() => {
    // Start with provider filter, then search (for stats calculation)
    let baseFiltered = patients;

    // Apply provider filter: show patients matching selected providers, or those with no provider
    if (selectedProviders.length > 0) {
      baseFiltered = baseFiltered.filter(p =>
        !p.provider || !p.provider.trim() || selectedProviders.includes(p.provider.trim())
      );
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      baseFiltered = baseFiltered.filter(p =>
        p.name?.toLowerCase().includes(query) ||
        p.email?.toLowerCase().includes(query) ||
        p.phone?.includes(query)
      );
    }

    const now = dayjs();

    // Helper: Check if patient is contacted
    const isContacted = (patient) => {
      return normalizeStatus(patient.status) === 'contacted';
    };

    // Helper: Check if patient is resolved (recovered or closed — exits the work queue)
    const isResolved = (patient) => {
      const ns = normalizeStatus(patient.status);
      return ns === 'recovered' || ns === 'closed';
    };

    // Helper: Check if patient needs follow-up
    const needsFollowUp = (patient) => {
      const ns = normalizeStatus(patient.status);

      // Explicitly marked as follow_up
      if (ns === 'follow_up') return true;

      // Contacted 3+ days ago with no response
      if (ns === 'contacted') {
        const lastTouch = patient.lastTouch || patient.last_touch;
        const daysSinceContact = lastTouch ? now.diff(dayjs(lastTouch), 'day') : 0;
        return daysSinceContact >= FOLLOW_UP_DAYS;
      }

      return false;
    };

    // Sort by oldest first (needs attention most)
    const sortByOldest = (list) => [...list].sort((a, b) => {
      const aDate = new Date(a.lastTouch || a.last_touch || 0).getTime();
      const bDate = new Date(b.lastTouch || b.last_touch || 0).getTime();
      return aDate - bDate;
    });

    // Calculate stats from base filtered (before contacted filter)
    const allActivePatients = baseFiltered.filter(p => !isResolved(p));
    const totalContacted = allActivePatients.filter(isContacted).length;
    const allRespondedPatients = baseFiltered.filter(isResolved);

    // Calculate follow-up count from all patients (for stats)
    const allFollowUp = allActivePatients.filter(needsFollowUp);

    // Apply active filter (for display only)
    let filtered = baseFiltered;
    if (activeFilter === 'contacted') {
      filtered = filtered.filter(p => isContacted(p) || needsFollowUp(p));
    } else if (activeFilter === 'toContact') {
      filtered = filtered.filter(p => !isContacted(p) && !isResolved(p));
    } else if (activeFilter === 'responded') {
      filtered = filtered.filter(isResolved);
    } else if (activeFilter === 'followUp') {
      filtered = filtered.filter(needsFollowUp);
    }

    // First, filter out resolved patients (responded/done/closed exit the work queue)
    const activePatients = filtered.filter(p => !isResolved(p));

    // Separate out follow-up patients
    const followUp = sortByOldest(activePatients.filter(needsFollowUp));
    const followUpIds = new Set(followUp.map(p => p.id));

    // Remaining patients (not in follow-up) go to their risk groups
    const remaining = activePatients.filter(p => !followUpIds.has(p.id));

    const urgent = sortByOldest(remaining.filter(p => p.risk?.toLowerCase() === 'high'));
    const medium = sortByOldest(remaining.filter(p => p.risk?.toLowerCase() === 'medium'));
    const low = sortByOldest(remaining.filter(p => p.risk?.toLowerCase() === 'low'));

    // Responded patients (for the success section)
    const responded = sortByOldest(filtered.filter(isResolved));

    return {
      followUpPatients: followUp,
      urgentPatients: urgent,
      mediumPatients: medium,
      lowPatients: low,
      respondedPatients: responded,
      stats: {
        total: allActivePatients.length,
        contacted: totalContacted,
        remaining: allActivePatients.length - totalContacted,
        urgent: allActivePatients.filter(p => p.risk?.toLowerCase() === 'high' && !allFollowUp.some(f => f.id === p.id)).length,
        followUp: allFollowUp.length,
        resolved: allRespondedPatients.length,
      }
    };
  }, [patients, searchQuery, activeFilter, selectedProviders]);
}
